/**
 * MONSTER TOWN — ROUTINES AND GENTLE NEEDS. FROZEN CONTRACT (src/core/*).
 * Additive only.
 *
 * TWO THINGS LIVE HERE AND THEY ARE DELIBERATELY IN THE SAME FILE, because they
 * are the same idea seen from two sides: the world has a rhythm, and so does the
 * kid's own monster.
 *
 *  1. ROUTINE TABLES — what each neighbour is doing at a given hour. Pure data,
 *     resolved against `src/core/time.ts`. A new neighbour, or a new venue's
 *     staff, is a table in `src/content/routines.ts` and nothing else.
 *
 *  2. NEEDS — "a little sleepy at night, a little hungry in the morning".
 *
 * ===========================================================================
 *  READ THIS BEFORE YOU TOUCH `needs`. IT IS THE LAW, AND IT IS ENFORCED BY
 *  THE SHAPE OF THE TYPES, NOT BY GOOD INTENTIONS.
 * ===========================================================================
 *
 * A need in this game is an INVITATION. It is never a meter, never a timer,
 * never a punishment. Concretely, and permanently:
 *
 *  - `warmth` is DERIVED from the clock every time you ask. Nothing accumulates,
 *    so nothing can drain, so there is no empty to reach.
 *  - There is no `NeedState`, no `value`, no `decay`, no `critical`, no `sad`,
 *    no `blocked`. A crew that wanted to punish a kid for ignoring breakfast
 *    would have to invent a field to punish them with, and adding one to core is
 *    a change to a frozen contract, which is a conversation, not a commit.
 *  - `satisfy()` returns only a NON-NEGATIVE bonus. Its return type cannot
 *    express a loss, and the implementation clamps at 0 anyway.
 *  - Nothing here gates anything. There is no API that answers "may the kid do
 *    X" — so a need cannot block a door, an errand, a build, or a venue.
 *
 * Eating breakfast and sleeping feel good. Not doing either costs exactly
 * nothing, forever (BRIEF law #2).
 */

import './dayEvents'
import { bus } from './events'
import { clock, hourToT, type TimePhaseId, type TimeSnapshot } from './time'

// ---------------------------------------------------------------------------
// routines — DATA
// ---------------------------------------------------------------------------

/**
 * What a monster is doing. Deliberately a closed set: the NPC crew maps each of
 * these onto a behaviour, and a content drop that invented its own string would
 * silently do nothing.
 */
export type RoutineActivity =
  | 'asleep'
  | 'waking'
  | 'breakfast'
  | 'out'
  | 'shopping'
  | 'visiting'
  | 'working'
  | 'dinner'
  | 'bath'
  | 'winddown'
  | 'bed'

export interface RoutineEntry {
  /** Hour 0..24 (may be fractional) at which this activity BEGINS. */
  readonly at: number
  readonly activity: RoutineActivity
  /**
   * Where. A scene id, or a landmark key the world crew knows. Data — this file
   * never interprets it.
   */
  readonly where?: string
  /** Optional voice key / audio cue for arriving at this activity. */
  readonly cue?: string
}

export interface RoutineTable {
  /** NPC id, or a ROLE id that several NPCs share ('shopkeeper', 'zookeeper'). */
  readonly id: string
  readonly devName: string
  /** Need not be sorted; the resolver sorts. Wraps midnight automatically. */
  readonly entries: readonly [RoutineEntry, ...RoutineEntry[]]
}

export interface ResolvedRoutine {
  readonly id: string
  readonly activity: RoutineActivity
  readonly where: string | null
  /** Hour this activity started, and the hour the next one starts. */
  readonly since: number
  readonly until: number
  /** 0..1 through this activity. */
  readonly t: number
  readonly cue: string | null
}

const TABLES = new Map<string, { table: RoutineTable; sorted: RoutineEntry[] }>()

export function registerRoutine(table: RoutineTable): RoutineTable {
  const sorted = [...table.entries].sort((a, b) => a.at - b.at)
  TABLES.set(table.id, { table, sorted })
  return table
}

export function registerRoutines(tables: readonly RoutineTable[]): void {
  for (const t of tables) registerRoutine(t)
}

export function getRoutine(id: string): RoutineTable | undefined {
  return TABLES.get(id)?.table
}

export function routineIds(): string[] {
  return [...TABLES.keys()]
}

/** Type-checked authoring helper — mirrors `definePlaceable`. */
export function defineRoutine(t: RoutineTable): RoutineTable {
  return t
}

/**
 * What is `id` doing at `hour`? Pure, total, and wraps midnight — a table whose
 * first entry is 07:00 has its LAST entry running through the small hours, which
 * is what "asleep at 3am" means.
 */
export function routineAt(id: string, hour: number): ResolvedRoutine | null {
  const rec = TABLES.get(id)
  if (rec === undefined) return null
  const list = rec.sorted
  const h = ((hour % 24) + 24) % 24
  let i = list.length - 1
  for (let k = 0; k < list.length; k++) {
    if (list[k]!.at <= h) i = k
    else break
  }
  // Before the first entry of the day -> still yesterday's last activity.
  if (h < list[0]!.at) i = list.length - 1
  const cur = list[i]!
  const next = list[(i + 1) % list.length]!
  const since = cur.at
  const until = next.at <= since ? next.at + 24 : next.at
  const hh = h >= since ? h : h + 24
  const span = until - since || 24
  return {
    id,
    activity: cur.activity,
    where: cur.where ?? null,
    since,
    until,
    t: Math.max(0, Math.min(1, (hh - since) / span)),
    cue: cur.cue ?? null,
  }
}

export function routineNow(id: string): ResolvedRoutine | null {
  return routineAt(id, clock.hour)
}

/** Everyone's current activity, in one pass — the NPC crew's per-tick read. */
export function allRoutinesNow(): ResolvedRoutine[] {
  const h = clock.hour
  const out: ResolvedRoutine[] = []
  for (const id of TABLES.keys()) {
    const r = routineAt(id, h)
    if (r !== null) out.push(r)
  }
  return out
}

// ---------------------------------------------------------------------------
// routine change notifications
// ---------------------------------------------------------------------------

export interface RoutineChange {
  readonly id: string
  readonly activity: RoutineActivity
  readonly previous: RoutineActivity
  readonly where: string | null
}

const lastActivity = new Map<string, RoutineActivity>()
const routineFns = new Set<(c: RoutineChange) => void>()

export function onRoutineChange(fn: (c: RoutineChange) => void): () => void {
  routineFns.add(fn)
  return () => routineFns.delete(fn)
}

/** Called by the clock tick installed below; exported for deterministic tests. */
export function pumpRoutines(hour = clock.hour): RoutineChange[] {
  const changes: RoutineChange[] = []
  for (const id of TABLES.keys()) {
    const r = routineAt(id, hour)
    if (r === null) continue
    const prev = lastActivity.get(id)
    if (prev === r.activity) continue
    lastActivity.set(id, r.activity)
    if (prev === undefined) continue // first sight is not a change
    const c: RoutineChange = { id, activity: r.activity, previous: prev, where: r.where }
    changes.push(c)
    for (const fn of [...routineFns]) {
      try {
        fn(c)
      } catch (err) {
        console.error('[schedule] routine listener threw', err)
      }
    }
    bus.emit('routine:changed', c)
  }
  return changes
}

/** Forget who was doing what — used after a slot switch or a sleep. */
export function resetRoutineMemory(): void {
  lastActivity.clear()
}

// ---------------------------------------------------------------------------
// needs — INVITATIONS ONLY
// ---------------------------------------------------------------------------

export type NeedId = 'sleepy' | 'hungry'

/**
 * A need, as the game is allowed to express it.
 *
 * NOTE WHAT IS NOT HERE: no value, no level, no decay rate, no threshold, no
 * mood, no "unmet". `warmth` is how brightly the game is *inviting*, computed
 * fresh from the clock. Ignore it and it simply goes away when the hour passes.
 */
export interface NeedInvite {
  readonly id: NeedId
  /** 0..1 — how strongly the world is suggesting this right now. */
  readonly warmth: number
  /** Convenience: `warmth > 0.35`. Nothing behaves differently below it. */
  readonly inviting: boolean
  /** Icon key. A picture of the THING (bed, bowl) — never a face (LAW #4). */
  readonly icon: string
  /** Where the invitation points: a placeable category the HUD can arrow at. */
  readonly points: string
}

/**
 * When each need is warm, as DATA. A band is [fromHour, toHour); it wraps.
 * Warmth eases in and out so nothing ever pops on.
 */
export interface NeedWindow {
  readonly id: NeedId
  readonly fromHour: number
  readonly toHour: number
  readonly icon: string
  readonly points: string
  /** Coins for accepting the invitation. NEVER negative — see the header. */
  readonly bonusCoins: number
  /** Only warm in these phases, if given. */
  readonly phases?: readonly TimePhaseId[]
}

export const DEFAULT_NEED_WINDOWS: readonly NeedWindow[] = [
  { id: 'hungry', fromHour: 6, toHour: 11, icon: 'bowl', points: 'kitchen', bonusCoins: 5 },
  { id: 'sleepy', fromHour: 20, toHour: 6, icon: 'bed', points: 'bed', bonusCoins: 5 },
]

class Needs {
  private windows: readonly NeedWindow[] = DEFAULT_NEED_WINDOWS
  /** The last in-world DAY each need was accepted. Only used to pay the bonus once. */
  private acceptedOn = new Map<NeedId, number>()
  private fns = new Set<(n: NeedInvite) => void>()
  private lastInviting = new Map<NeedId, boolean>()

  setWindows(w: readonly NeedWindow[]): void {
    if (w.length > 0) this.windows = w
  }
  windowsTable(): readonly NeedWindow[] {
    return this.windows
  }

  private warmthOf(w: NeedWindow, snap: TimeSnapshot): number {
    if (w.phases !== undefined && !w.phases.includes(snap.phase)) return 0
    const t = snap.t
    const from = hourToT(w.fromHour)
    const to = hourToT(w.toHour)
    const span = to > from ? to - from : to + 1 - from
    if (span <= 0) return 0
    const local = t >= from ? t - from : t + 1 - from
    if (local > span) return 0
    const u = local / span
    // Ease in over the first fifth, out over the last fifth. Never a step.
    const rise = Math.min(1, u / 0.2)
    const fall = Math.min(1, (1 - u) / 0.2)
    const e = Math.min(rise, fall)
    return e * e * (3 - 2 * e)
  }

  invite(id: NeedId): NeedInvite {
    const snap = clock.now()
    const w = this.windows.find((x) => x.id === id)
    if (w === undefined) {
      return { id, warmth: 0, inviting: false, icon: 'star', points: '' }
    }
    const warmth = this.warmthOf(w, snap)
    return { id, warmth, inviting: warmth > 0.35, icon: w.icon, points: w.points }
  }

  /** Every need, warm or not. The HUD shows only the warm ones. */
  now(): NeedInvite[] {
    return this.windows.map((w) => this.invite(w.id))
  }

  /**
   * The kid ate / slept. This is the ONLY mutation, and all it can do is hand
   * out a small, non-negative "that felt good" bonus — once per in-world day per
   * need, so it is a nice moment rather than a coin faucet.
   *
   * Accepting a need that is not warm is fine and returns 0. Nothing scolds.
   */
  satisfy(id: NeedId, source: string): { bonusCoins: number; wasInvited: boolean } {
    const w = this.windows.find((x) => x.id === id)
    const inv = this.invite(id)
    const day = clock.day
    const already = this.acceptedOn.get(id) === day
    const bonus = !already && inv.inviting ? Math.max(0, w?.bonusCoins ?? 0) : 0
    if (bonus > 0) this.acceptedOn.set(id, day)
    bus.emit('need:satisfied', { id, source, bonusCoins: bonus, wasInvited: inv.inviting })
    return { bonusCoins: bonus, wasInvited: inv.inviting }
  }

  /** Fires when a need becomes warm. Never fires "you failed to" anything. */
  onInvite(fn: (n: NeedInvite) => void): () => void {
    this.fns.add(fn)
    return () => this.fns.delete(fn)
  }

  /** Driven by the clock tick; exported through `pumpNeeds` for tests. */
  pump(): void {
    for (const n of this.now()) {
      const was = this.lastInviting.get(n.id) ?? false
      if (n.inviting === was) continue
      this.lastInviting.set(n.id, n.inviting)
      if (!n.inviting) continue
      for (const fn of [...this.fns]) {
        try {
          fn(n)
        } catch (err) {
          console.error('[schedule] need listener threw', err)
        }
      }
      bus.emit('need:invited', n)
    }
  }

  snapshot(): NeedsSaveState {
    return { acceptedOn: Object.fromEntries(this.acceptedOn) as Record<string, number> }
  }

  restore(state: NeedsSaveState): void {
    this.acceptedOn.clear()
    for (const [k, v] of Object.entries(state.acceptedOn ?? {})) {
      if (k === 'sleepy' || k === 'hungry') this.acceptedOn.set(k, Math.max(0, Math.round(v)))
    }
    this.lastInviting.clear()
  }

  resetForTest(): void {
    this.acceptedOn.clear()
    this.lastInviting.clear()
    this.fns.clear()
    this.windows = DEFAULT_NEED_WINDOWS
  }
}

export interface NeedsSaveState {
  acceptedOn: Record<string, number>
}

export const needs = new Needs()

export function pumpNeeds(): void {
  needs.pump()
}

// ---------------------------------------------------------------------------
// wiring
// ---------------------------------------------------------------------------

let installed = false

/**
 * Hangs routines and needs off the clock. Called once from `main.ts` right after
 * `installClock()`. Idempotent.
 */
export function installSchedule(): void {
  if (installed) return
  installed = true
  clock.onTick(() => {
    pumpRoutines()
    needs.pump()
  })
  // A sleep is a jump; nobody should be reported as "changing" through six
  // activities they were never seen doing.
  bus.on('time:slept', () => resetRoutineMemory())
  bus.on('save:loaded', () => resetRoutineMemory())
}
